import { useCallback, useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { chromeDeepLinkForUrl, isMacOS } from '../../utils/googleChromeDetection';

interface ChromeRequiredToastProps {
  isOpen: boolean;
  onDismiss: () => void;
}

export const ChromeRequiredToast: FC<ChromeRequiredToastProps> = ({ isOpen, onDismiss }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he' || i18n.language === 'ar';
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const showOpenInChrome = isMacOS();

  const openInChrome = useCallback(() => {
    const target = chromeDeepLinkForUrl(window.location.href);
    console.log('[ChromeToast] deep link:', target);
    window.location.href = target;
  }, []);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2500);
    } catch {
      setCopyState('error');
      window.setTimeout(() => setCopyState('idle'), 2500);
    }
  }, []);

  if (typeof document === 'undefined' || !document.body) {
    return null;
  }

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom,0px))] z-[100000] flex justify-center sm:inset-x-auto sm:left-1/2 sm:w-full sm:max-w-lg sm:-translate-x-1/2 sm:bottom-[max(1.5rem,env(safe-area-inset-bottom,0px))] isolate"
      role="presentation"
    >
      <AnimatePresence mode="sync">
        {isOpen && (
          <motion.div
            key="chrome-browser-toast"
            role="region"
            aria-label={t('browserChrome.title')}
            aria-live="polite"
            initial={{ opacity: 0, y: 28, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 420, damping: 32, mass: 0.85 }}
            className="pointer-events-auto w-full max-w-lg rounded-xl border-2 border-blue-500 bg-white text-neutral-900 shadow-2xl ring-2 ring-blue-600/30 dark:border-blue-400 dark:bg-zinc-900 dark:text-neutral-100 dark:ring-blue-400/25"
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            <div className="h-0.5 rounded-t-xl bg-gradient-to-r from-blue-600 to-indigo-600" />

            <div className="p-4">
              <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="flex-shrink-0 pt-0.5">
                  <InformationCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <div className={`flex items-start gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <h2
                      id="chrome-required-title"
                      className="flex-1 text-sm font-semibold leading-snug text-neutral-900 dark:text-white"
                    >
                      {t('browserChrome.title')}
                    </h2>
                    <button
                      type="button"
                      onClick={onDismiss}
                      className="flex-shrink-0 rounded-lg p-1 text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-zinc-800 dark:hover:text-neutral-200"
                      aria-label={t('browserChrome.close')}
                    >
                      <XMarkIcon className="h-5 w-5" aria-hidden />
                    </button>
                  </div>
                  <p
                    id="chrome-required-desc"
                    className="mt-1.5 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300"
                  >
                    {t('browserChrome.message')}
                  </p>

                  <div
                    className={`mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap ${isRTL ? 'sm:flex-row-reverse' : ''}`}
                  >
                    {showOpenInChrome && (
                      <button
                        type="button"
                        onClick={openInChrome}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 sm:flex-none sm:min-w-[9rem]"
                      >
                        <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0" aria-hidden />
                        <span>{t('browserChrome.openInChrome')}</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={copyLink}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 transition hover:bg-neutral-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-neutral-100 dark:hover:bg-zinc-800 sm:flex-none sm:min-w-[9rem]"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4 shrink-0" aria-hidden />
                      <span>
                        {copyState === 'copied'
                          ? t('browserChrome.copied')
                          : copyState === 'error'
                            ? t('browserChrome.copyFailed')
                            : t('browserChrome.copyLink')}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    document.body
  );
};
